import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl, getWsUrl } from '../utils/api';

// Get WebSocket URL dynamically - use env var on localhost, origin elsewhere (custom domain support)
const getWsUrlDynamic = () => {
  if (typeof window !== 'undefined') {
    // On localhost (development), use the env variable
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
    }
    // On any other domain (production/preview/custom domain), use current origin
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  return process.env.REACT_APP_BACKEND_URL
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
};

// Get API URL dynamically - use env var on localhost, origin elsewhere (custom domain support)
const getApiUrlDynamic = () => {
  if (typeof window !== 'undefined') {
    // On localhost (development), use the env variable
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL;
    }
    // On any other domain (production/preview/custom domain), use current origin
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL;
};

export default function useWebSocket(token, onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Reduced since we have polling fallback
  const pollingRef = useRef(null);
  const lastRouteDataRef = useRef(null);

  // Polling fallback for when WebSocket isn't available
  const startPolling = useCallback(() => {
    if (pollingRef.current) return; // Already polling
    
    const API_URL = getApiUrlDynamic();
    console.log('Starting polling fallback for sync');
    pollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/active-route`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Check if data changed
          const newDataStr = JSON.stringify(data);
          if (newDataStr !== lastRouteDataRef.current) {
            lastRouteDataRef.current = newDataStr;
            onMessage?.({ type: 'route_update', data });
            
            // Also check driver location
            if (data.driver_location) {
              onMessage?.({ type: 'driver_location', data: data.driver_location });
            }
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000); // Poll every 5 seconds
  }, [token, onMessage]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const WS_URL = getWsUrlDynamic();
      const ws = new WebSocket(`${WS_URL}/api/ws/sync`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected, sending auth...');
        // Send token for authentication
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            console.log('WebSocket authenticated as:', data.role);
            setIsConnected(true);
            reconnectAttempts.current = 0;
            stopPolling(); // Stop polling since WS is working
          } else if (data.type === 'error') {
            console.error('WebSocket auth error:', data.message);
            ws.close();
          } else {
            // Forward message to handler
            onMessage?.(data);
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with fallback to polling
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else {
          console.log('WebSocket reconnection failed, falling back to polling');
          startPolling();
          setIsConnected(true); // Mark as "connected" via polling
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Start polling on error
        if (reconnectAttempts.current >= maxReconnectAttempts - 1) {
          startPolling();
          setIsConnected(true);
        }
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      startPolling();
      setIsConnected(true);
    }
  }, [token, onMessage, startPolling, stopPolling]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    stopPolling();
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [stopPolling]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  // Connect when token is available
  useEffect(() => {
    if (token) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  return { isConnected, send, disconnect };
}
